import React, { useCallback, useEffect, useState } from 'react';
import { Bot, FileDiff, CheckCircle2, XCircle, ScanLine, TrendingUp } from 'lucide-react';
import {
  AutonomyService,
  type AutonomousActivity,
  type AutonomySettings,
} from '../../services/autonomyService';

const LEVELS: { value: AutonomySettings['autonomyLevel']; label: string; hint: string }[] = [
  { value: 'observe', label: 'Observe', hint: 'Scan only — no changes' },
  { value: 'propose', label: 'Propose', hint: 'Open patches for your review' },
  { value: 'auto_patch', label: 'Auto-patch', hint: 'Apply patches on cadence (local)' },
];

function ActivityIcon({ kind }: { kind: AutonomousActivity['kind'] }) {
  if (kind === 'scan') return <ScanLine className="w-4 h-4 text-gray-400" />;
  if (kind === 'patch_proposed') return <FileDiff className="w-4 h-4 text-teal-500" />;
  if (kind === 'patch_approved') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
  return <XCircle className="w-4 h-4 text-red-400" />;
}

function timeAgo(iso: string): string {
  const days = Math.round((Date.parse('2026-06-11T18:00:00Z') - Date.parse(iso)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export const AutonomyPanel: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const [settings, setSettings] = useState<AutonomySettings | null>(null);
  const [activity, setActivity] = useState<AutonomousActivity[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await AutonomyService.get(repositoryId);
      setSettings(res.data.settings);
      setActivity(res.data.activity);
    } catch {
      // Repo may predate autonomy seeding; panel just stays empty.
    }
  }, [repositoryId]);

  useEffect(() => {
    load();
  }, [load]);

  const setLevel = async (level: AutonomySettings['autonomyLevel']) => {
    if (!settings || saving) return;
    setSaving(true);
    setSettings({ ...settings, autonomyLevel: level });
    try {
      await AutonomyService.update(repositoryId, { autonomyLevel: level });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <section className="mt-6 p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2 mb-4">
        <Bot className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        <h2 className="font-semibold text-gray-900 dark:text-white">Autonomous maintenance</h2>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-6">
        {LEVELS.map((l) => {
          const active = settings.autonomyLevel === l.value;
          return (
            <button
              key={l.value}
              onClick={() => setLevel(l.value)}
              disabled={saving}
              className={`text-left p-3 rounded-lg border transition-colors ${
                active
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-900 dark:text-white">{l.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{l.hint}</div>
            </button>
          );
        })}
      </div>

      {activity.length > 0 && (
        <>
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Autonomous activity
          </h3>
          <ul className="space-y-2">
            {activity.map((a) => (
              <li key={a.id} className="flex items-center gap-3 text-sm">
                <ActivityIcon kind={a.kind} />
                <span className="flex-1 text-gray-800 dark:text-gray-200">{a.summary}</span>
                {a.scoreDelta != null && (
                  <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400">
                    <TrendingUp className="w-3 h-3" />+{a.scoreDelta}
                  </span>
                )}
                <span className="text-xs text-gray-400 w-16 text-right">
                  {timeAgo(a.occurredAt)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};
